/* eslint-disable @typescript-eslint/naming-convention */
import {
  IProductApiModel,
  IProductImageGallery,
  IProductMediaImages,
  IVariationApiModel,
} from '../repositories/IStore.api';
import { CategoryModel } from './Category.model';

export class ProductModel implements ProductModelProps {
  id: number;
  sku: string;
  quantity: number;
  unlimited: boolean;
  inStock: boolean;
  name: string;
  nameTranslated?: {
    es_419: string;
  };
  price: number;
  priceInProductList: number;
  defaultDisplayedPrice: number;
  defaultDisplayedPriceFormatted: string;
  tax?: {
    taxable: boolean;
    defaultLocationIncludedTaxRate?: number;
    enabledManualTaxes?: [];
    taxClassCode?: string;
  };
  compareToPrice: number;
  compareToPriceFormatted: string;
  compareToPriceDiscount: number;
  compareToPriceDiscountFormatted: string;
  compareToPriceDiscountPercent: number;
  compareToPriceDiscountPercentFormatted: string;
  isShippingRequired: boolean;
  url: string;
  created: string;
  updated: string;
  createTimestamp: number;
  updateTimestamp: number;
  productClassId: number;
  enabled: boolean;
  options?: any[];
  warningLimit: number;
  fixedShippingRateOnly: boolean;
  fixedShippingRate: number;
  shipping: {
    type: string;
    methodMarkup: number;
    flatRate: number;
    disabledMethods?: any;
    enabledMethods?: any;
  };
  defaultCombinationId: number;
  description: string;
  descriptionTranslated: {
    es_419: string;
  };
  galleryImages: IProductImageGallery[];
  media?: {
    images?: IProductMediaImages[];
  };
  categoryIds?: number[];
  categories?: any[];
  subcategory?: any[];
  defaultCategoryId?: number;
  seoTitle?: string;
  seoDescription?: string;
  attributes?: any;
  files?: any;
  relatedProducts?: {
    productIds?: any;
    relatedCategory?: {
      enabled: boolean;
      categoryId: number;
      productCount: number;
    };
  };
  combinations?: any;
  isSampleProduct: boolean;
  googleItemCondition: string;
  isGiftCard: boolean;
  discountsAllowed: boolean;
  nameYourPriceEnabled: boolean;
  productCondition: string;
  status?: string;
  shipping_required?: boolean;
  weight?: number;
  imageList?: IProductImages[];

  constructor(data: ProductModelProps) {
    this.id = data.id;
    this.sku = data.sku;
    this.quantity = data.quantity;
    this.unlimited = data.unlimited;
    this.inStock = data.inStock;
    this.name = data.name;
    this.nameTranslated = data.nameTranslated
      ? {
          es_419: data.nameTranslated.es_419,
        }
      : null;
    this.price = data.price;
    this.priceInProductList = data.priceInProductList;
    this.defaultDisplayedPrice = data.defaultDisplayedPrice;
    this.defaultDisplayedPriceFormatted = data.defaultDisplayedPriceFormatted;
    this.tax = data.tax
      ? {
          taxable: data.tax.taxable,
          defaultLocationIncludedTaxRate:
            data.tax.defaultLocationIncludedTaxRate,
          enabledManualTaxes: data.tax.enabledManualTaxes,
          taxClassCode: data.tax.taxClassCode,
        }
      : null;
    this.compareToPrice = data.compareToPrice;
    this.compareToPriceFormatted = data.compareToPriceFormatted;
    this.compareToPriceDiscount = data.compareToPriceDiscount;
    this.compareToPriceDiscountFormatted = data.compareToPriceDiscountFormatted;
    this.compareToPriceDiscountPercent = data.compareToPriceDiscountPercent;
    this.compareToPriceDiscountPercentFormatted =
      data.compareToPriceDiscountPercentFormatted;
    this.isShippingRequired = data.isShippingRequired;
    this.url = data.url;
    this.created = data.created;
    this.updated = data.updated;
    this.createTimestamp = data.createTimestamp;
    this.updateTimestamp = data.updateTimestamp;
    this.productClassId = data.productClassId;
    this.enabled = data.enabled;
    this.options = data.options;
    this.warningLimit = data.warningLimit;
    this.fixedShippingRateOnly = data.fixedShippingRateOnly;
    this.fixedShippingRate = data.fixedShippingRate;
    this.shipping = data.shipping
      ? {
          type: data.shipping.type,
          methodMarkup: data.shipping.methodMarkup,
          flatRate: data.shipping.flatRate,
          disabledMethods: data.shipping.disabledMethods,
          enabledMethods: data.shipping.enabledMethods,
        }
      : null;
    this.defaultCombinationId = data.defaultCombinationId;
    this.description = data.description;
    this.descriptionTranslated = data.descriptionTranslated
      ? {
          es_419: data.descriptionTranslated.es_419,
        }
      : null;
    this.galleryImages = data.galleryImages;
    this.media = data.media
      ? {
          images: data.media.images,
        }
      : null;
    this.categoryIds = data.categoryIds;
    this.categories = data.categories ? data.categories : null;
    this.defaultCategoryId = data.defaultCategoryId;
    this.seoTitle = data.seoTitle;
    this.seoDescription = data.seoDescription;
    this.attributes = data.attributes;
    this.files = data.files;
    this.relatedProducts = data.relatedProducts
      ? {
          productIds: data.relatedProducts.productIds,
          relatedCategory: data.relatedProducts.relatedCategory
            ? {
                enabled: data.relatedProducts.relatedCategory.enabled,
                categoryId: data.relatedProducts.relatedCategory.categoryId,
                productCount: data.relatedProducts.relatedCategory.productCount,
              }
            : null,
        }
      : null;
    this.combinations = data.combinations;
    this.isSampleProduct = data.isSampleProduct;
    this.googleItemCondition = data.googleItemCondition;
    this.isGiftCard = data.isGiftCard;
    this.discountsAllowed = data.discountsAllowed;
    this.nameYourPriceEnabled = data.nameYourPriceEnabled;
    this.productCondition = data.productCondition;
  }

  static fromApi(data: IProductApiModel) {
    return {
      id: data.id,
      sku: data.sku,
      quantity: data.quantity,
      unlimited: data.unlimited,
      inStock: data.inStock,
      name: data.name,
      nameTranslated: data.nameTranslated
        ? {
            es_419: data.nameTranslated.es_419,
          }
        : null,
      price: data.price,
      priceInProductList: data.priceInProductList,
      defaultDisplayedPrice: data.defaultDisplayedPrice,
      defaultDisplayedPriceFormatted: data.defaultDisplayedPriceFormatted,
      tax: data.tax
        ? {
            taxable: data.tax.taxable,
            defaultLocationIncludedTaxRate:
              data.tax.defaultLocationIncludedTaxRate,
            enabledManualTaxes: data.tax.enabledManualTaxes,
            taxClassCode: data.tax.taxClassCode,
          }
        : null,
      compareToPrice: data.compareToPrice,
      compareToPriceFormatted: data.compareToPriceFormatted,
      compareToPriceDiscount: data.compareToPriceDiscount,
      compareToPriceDiscountFormatted: data.compareToPriceDiscountFormatted,
      compareToPriceDiscountPercent: data.compareToPriceDiscountPercent,
      compareToPriceDiscountPercentFormatted:
        data.compareToPriceDiscountPercentFormatted,
      isShippingRequired: data.isShippingRequired,
      url: data.url,
      created: data.created,
      updated: data.updated,
      createTimestamp: data.createTimestamp,
      updateTimestamp: data.updateTimestamp,
      productClassId: data.productClassId,
      enabled: data.enabled,
      options: data.options,
      warningLimit: data.warningLimit,
      fixedShippingRateOnly: data.fixedShippingRateOnly,
      fixedShippingRate: data.fixedShippingRate,
      shipping: data.shipping
        ? {
            type: data.shipping.type,
            methodMarkup: data.shipping.methodMarkup,
            flatRate: data.shipping.flatRate,
            disabledMethods: data.shipping.disabledMethods,
            enabledMethods: data.shipping.enabledMethods,
          }
        : null,
      defaultCombinationId: data.defaultCombinationId,
      description: data.description,
      descriptionTranslated: data.descriptionTranslated
        ? {
            es_419: data.descriptionTranslated.es_419,
          }
        : null,
      galleryImages: data.galleryImages,
      media: data.media
        ? {
            images: data.media.images,
          }
        : null,
      categoryIds: data.categoryIds,
      categories: data.categories ? data.categories : null,
      defaultCategoryId: data.defaultCategoryId,
      seoTitle: data.seoTitle,
      seoDescription: data.seoDescription,
      attributes: data.attributes,
      files: data.files,
      relatedProducts: data.relatedProducts
        ? {
            productIds: data.relatedProducts.productIds,
            relatedCategory: data.relatedProducts.relatedCategory
              ? {
                  enabled: data.relatedProducts.relatedCategory.enabled,
                  categoryId: data.relatedProducts.relatedCategory.categoryId,
                  productCount:
                    data.relatedProducts.relatedCategory.productCount,
                }
              : null,
          }
        : null,
      combinations: data.combinations,
      isSampleProduct: data.isSampleProduct,
      googleItemCondition: data.googleItemCondition,
      isGiftCard: data.isGiftCard,
      discountsAllowed: data.discountsAllowed,
      nameYourPriceEnabled: data.nameYourPriceEnabled,
      productCondition: data.productCondition,
    };
  }

  static fromApiSingle(data: IProductApiModel, id: number) {
    return ({
      id,
      sku: data.sku,
      quantity: data.quantity,
      unlimited: data.unlimited,
      inStock: data.inStock,
      name: data.name,
      nameTranslated: data.nameTranslated ? {
        es_419: data.nameTranslated.es_419
      } : null,
      price: data.price,
      priceInProductList: data.priceInProductList,
      defaultDisplayedPrice: data.defaultDisplayedPrice,
      defaultDisplayedPriceFormatted: data.defaultDisplayedPriceFormatted,
      tax: data.tax ? {
        taxable: data.tax.taxable,
        defaultLocationIncludedTaxRate: data.tax.defaultLocationIncludedTaxRate,
        enabledManualTaxes: data.tax.enabledManualTaxes,
        taxClassCode: data.tax.taxClassCode
      } : null,
      compareToPrice: data.compareToPrice,
      compareToPriceFormatted: data.compareToPriceFormatted,
      compareToPriceDiscount: data.compareToPriceDiscount,
      compareToPriceDiscountFormatted: data.compareToPriceDiscountFormatted,
      compareToPriceDiscountPercent: data.compareToPriceDiscountPercent,
      compareToPriceDiscountPercentFormatted: data.compareToPriceDiscountPercentFormatted,
      isShippingRequired: data.isShippingRequired,
      url: data.url,
      created: data.created,
      updated: data.updated,
      createTimestamp: data.createTimestamp,
      updateTimestamp: data.updateTimestamp,
      productClassId: data.productClassId,
      enabled: data.enabled,
      options: data.options,
      warningLimit: data.warningLimit,
      fixedShippingRateOnly: data.fixedShippingRateOnly,
      fixedShippingRate: data.fixedShippingRate,
      shipping: data.shipping ? {
        type: data.shipping.type,
        methodMarkup: data.shipping.methodMarkup,
        flatRate: data.shipping.flatRate,
        disabledMethods: data.shipping.disabledMethods,
        enabledMethods: data.shipping.enabledMethods,
      } : null,
      defaultCombinationId: data.defaultCombinationId,
      description: data.description,
      descriptionTranslated: data.descriptionTranslated ? {
        es_419: data.descriptionTranslated.es_419
      } : null,
      galleryImages: data.galleryImages,
      media: data.media ? {
        images: data.media.images
      } : null,
      categoryIds: data.categoryIds,
      categories: data.categories ? data.categories : null,
      defaultCategoryId: data.defaultCategoryId,
      seoTitle: data.seoTitle,
      seoDescription: data.seoDescription,
      attributes: data.attributes,
      files: data.files,
      relatedProducts: data.relatedProducts ? {
        productIds: data.relatedProducts.productIds,
        relatedCategory : data.relatedProducts.relatedCategory ? {
          enabled: data.relatedProducts.relatedCategory.enabled,
          categoryId: data.relatedProducts.relatedCategory.categoryId,
          productCount: data.relatedProducts.relatedCategory.productCount
        } : null,
      } : null,
      combinations: data.combinations,
      isSampleProduct: data.isSampleProduct,
      googleItemCondition: data.googleItemCondition,
      isGiftCard: data.isGiftCard,
      discountsAllowed: data.discountsAllowed,
      nameYourPriceEnabled: data.nameYourPriceEnabled,
      productCondition: data.productCondition,
    });
  }

  static empty() {
    return new ProductModel({
      id: null,
      sku: null,
      quantity: null,
      unlimited: null,
      inStock: null,
      name: null,
      nameTranslated: null,
      price: null,
      priceInProductList: null,
      defaultDisplayedPrice: null,
      defaultDisplayedPriceFormatted: null,
      tax: null,
      compareToPrice: null,
      compareToPriceFormatted: null,
      compareToPriceDiscount: null,
      compareToPriceDiscountFormatted: null,
      compareToPriceDiscountPercent: null,
      compareToPriceDiscountPercentFormatted: null,
      isShippingRequired: null,
      url: null,
      created: null,
      updated: null,
      createTimestamp: null,
      updateTimestamp: null,
      productClassId: null,
      enabled: null,
      options: [],
      warningLimit: null,
      fixedShippingRateOnly: null,
      fixedShippingRate: null,
      shipping: null,
      defaultCombinationId: null,
      description: null,
      descriptionTranslated: null,
      galleryImages: null,
      media: null,
      categoryIds: null,
      categories: null,
      defaultCategoryId: null,
      seoTitle: null,
      seoDescription: null,
      attributes: null,
      files: null,
      relatedProducts: null,
      combinations: null,
      isSampleProduct: null,
      googleItemCondition: null,
      isGiftCard: null,
      discountsAllowed: null,
      nameYourPriceEnabled: null,
      productCondition: null,
    });
  }
}

export interface ProductModelProps {
  id: number;
  sku: string;
  quantity: number;
  unlimited: boolean;
  inStock: boolean;
  name: string;
  nameTranslated?: {
    es_419: string;
  };
  price: number;
  priceInProductList: number;
  defaultDisplayedPrice: number;
  defaultDisplayedPriceFormatted: string;
  tax?: {
    taxable: boolean;
    defaultLocationIncludedTaxRate?: number;
    enabledManualTaxes?: [];
    taxClassCode?: string;
  };
  compareToPrice: number;
  compareToPriceFormatted: string;
  compareToPriceDiscount: number;
  compareToPriceDiscountFormatted: string;
  compareToPriceDiscountPercent: number;
  compareToPriceDiscountPercentFormatted: string;
  isShippingRequired: boolean;
  url: string;
  created: string;
  updated: string;
  createTimestamp: number;
  updateTimestamp: number;
  productClassId: number;
  enabled: boolean;
  options?: any;
  warningLimit: number;
  fixedShippingRateOnly: boolean;
  fixedShippingRate: number;
  shipping: {
    type: string;
    methodMarkup: number;
    flatRate: number;
    disabledMethods?: any;
    enabledMethods?: any;
  };
  defaultCombinationId: number;
  description: string;
  descriptionTranslated: {
    es_419: string;
  };
  galleryImages: IProductImageGallery[];
  media?: {
    images?: IProductMediaImages[];
  };
  categoryIds?: number[];
  categories?: any[];
  subcategory?: any[];
  defaultCategoryId?: number;
  seoTitle?: string;
  seoDescription?: string;
  attributes?: any;
  files?: any;
  relatedProducts?: {
    productIds?: any;
    relatedCategory?: {
      enabled: boolean;
      categoryId: number;
      productCount: number;
    };
  };
  combinations?: any;
  isSampleProduct: boolean;
  googleItemCondition: string;
  isGiftCard: boolean;
  discountsAllowed: boolean;
  nameYourPriceEnabled: boolean;
  productCondition: string;
  checked?: boolean;
}

export class ProductVariationModel implements ProductVariationModelProps {
  id: number;
  name: string;
  options: Array<{
    id: number;
    name: string;
    order: number;
    price: string;
    variationId: number;
  }>;
  required: boolean;
  resourceUri: string;
  shop?: number;
  shopId: number;

  constructor(data: ProductVariationModelProps) {
    this.id = data.id;
    this.name = data.name;
    this.options = data.options;
    this.required = data.required;
    this.resourceUri = data.resourceUri;
    this.shop = data.shop;
    this.shopId = data.shopId;
  }

  static fromApi(data: IVariationApiModel) {
    const options = data.options.map((o) => ({
      id: o.id,
      name: o.name,
      order: o.order,
      price: o.price,
      variationId: o.variation_id,
    }));

    return new ProductVariationModel({
      id: data.id,
      name: data.name,
      options,
      required: data.required,
      resourceUri: data.resource_uri,
      shop: data.shop,
      shopId: data.shop_id,
    });
  }

  static empty() {
    return new ProductVariationModel({
      id: null,
      name: null,
      options: null,
      required: null,
      resourceUri: null,
      shop: null,
      shopId: null,
    });
  }
}

export interface ProductVariationModelProps {
  id: number;
  name: string;
  options: Array<{
    id: number;
    name: string;
    order: number;
    price: string;
    variationId: number;
  }>;
  required: boolean;
  resourceUri: string;
  shop?: number;
  shopId: number;
}

export interface IProductImages {
  id?: number;
  idProduct?: number;
  main: boolean;
  url: string;
  isNew: boolean;
}

export interface IProductImageUpload {
  id: number;
  file: string;
  gallery: boolean;
}
